/**
 * Display formatting.
 *
 * Two rules hold everywhere in this application.
 *
 * ABSOLUTE TIMESTAMPS, NEVER RELATIVE
 *   A review trail that says "two days ago" is useless the moment it is
 *   disputed, and every screen here exists to support a dispute about who
 *   approved what and when. Times are printed in the reader's own zone with an
 *   explicit offset, so two reviewers comparing notes are looking at the same
 *   instant rather than two correct-looking local times.
 *
 * HASHES ARE SHOWN AT A FIXED LENGTH, NEVER TRUNCATED BY THE LAYOUT
 *   The approved content hash is the thing an approval is bound to, so it is
 *   printed in full where it matters and abbreviated to a stable twelve
 *   characters where it is only an identifier.
 */

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

export function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  const offsetMinutes = -parsed.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  const offset = `UTC${sign}${String(Math.trunc(absolute / 60)).padStart(2, '0')}:${String(
    absolute % 60,
  ).padStart(2, '0')}`;
  return `${DATE_TIME.format(parsed)} ${offset}`;
}

export function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : DATE_ONLY.format(parsed);
}

export function shortHash(value: string | null | undefined): string {
  if (typeof value !== 'string' || value === '') return '';
  return value.length <= 12 ? value : value.slice(0, 12);
}

const UNITS = ['B', 'kB', 'MB'] as const;

export function formatBytes(bytes: number): string {
  let size = bytes;
  let unit = 0;
  while (size >= 1000 && unit < UNITS.length - 1) {
    size /= 1000;
    unit += 1;
  }
  const rounded = unit === 0 ? String(size) : size.toFixed(1);
  return `${rounded} ${UNITS[unit]}`;
}

/**
 * The people of one organization, by `user_id`.
 *
 * A `Map` rather than a plain object because ids are uuids: an object would
 * need every read guarded against a key like `constructor`, and the guard is
 * exactly the sort of thing that is written once and then depended on.
 */
export type PeopleDirectory = ReadonlyMap<string, string>;

/** What a row with no human actor reads as. Never blank: a blank cell in an
 * audit trail is indistinguishable from an event that did not happen. */
export const NO_ACTOR_LABEL = 'No person recorded';

/**
 * What a person this deployment cannot name reads as.
 *
 * The two real cases are a member who has since left and whose account row is
 * gone, and a principal that was never a person at all. Neither can be told
 * apart from here, so neither is guessed at: the sentence says the truth that
 * is available, which is that no identity was resolved.
 */
export const UNRESOLVED_LABEL = 'Unknown person';

const EMPTY_DIRECTORY: PeopleDirectory = new Map<string, string>();

/**
 * Build a directory from the rows `GET /v1/people` returns.
 *
 * A duplicate `user_id` keeps the first row rather than the last, so the
 * directory does not depend on the order the server happened to send. The
 * server orders by address and `memberships` is unique on `(org_id, user_id)`,
 * so a duplicate is not expected — which is exactly why a silent last-wins
 * would be a rule nobody could have written down on purpose.
 */
export function peopleDirectory(
  rows: readonly { readonly user_id: string; readonly email: string }[],
): PeopleDirectory {
  const directory = new Map<string, string>();
  for (const row of rows) {
    if (!directory.has(row.user_id)) directory.set(row.user_id, row.email);
  }
  return directory;
}

/**
 * A person, resolved — or an admission that this one could not be.
 *
 * WHAT A DIRECTORY IS
 *   `user_id -> label`, for the people of the organization in the caller's
 *   token, read once per navigation from `GET /v1/people` and carried on
 *   `AppState.directory`. It is passed in rather than reached for through a
 *   module-level store so that a label is a function of data this render was
 *   given, and so that a view which is handed an empty directory renders the
 *   fallback rather than silently reading a different organization's answer.
 *
 * WHY THE DIRECTORY IS AN ARGUMENT AND NOT A MODULE-LEVEL CACHE
 *   A hidden store makes `personLabel` impure: the same call returns different
 *   strings depending on what some earlier request happened to load, so a test
 *   can pass while a screen shows a previous organization's name, and there is
 *   no place to assert that the store is org-scoped at all. Passing it puts the
 *   org scoping in the type of the thing being rendered.
 *
 * THE THREE ANSWERS, AND WHY NONE OF THEM IS A UUID
 *   - no id at all: the row records an event with no human actor (`actor_kind`
 *     is `system`, or the grant is organization-wide), so there is nobody to
 *     name;
 *   - an id that resolves: the address the identity provider holds, which is
 *     what every other screen in this application already calls a person — the
 *     invitations panel prints `invite.email` in full, and the invite form asks
 *     for a full address. A local part or an "initial plus surname" would be a
 *     NEW convention invented here, derived from a string, for a table that
 *     stores no name at all;
 *   - an id that does not resolve: someone who has left the organization and
 *     whose account row is gone, or a principal that was never a person (a
 *     group, a client). Both are real, both are common, and `Unknown person` is
 *     the sentence for them. It is stated rather than blank because a blank
 *     cell in an audit trail reads as "nothing happened"; and it is stated
 *     rather than a truncated uuid because eight hex characters identify a
 *     principal to a database and to no person on earth.
 */
export function personLabel(
  userId: string | null | undefined,
  directory: PeopleDirectory = EMPTY_DIRECTORY,
): string {
  if (typeof userId !== 'string' || userId === '') return NO_ACTOR_LABEL;
  return directory.get(userId) ?? UNRESOLVED_LABEL;
}

/** V1 section 6 lifecycle: a skill's state is the word a person would use. */
export const LIFECYCLE_LABEL: Readonly<Record<string, string>> = {
  active: 'Active',
  deprecated: 'Deprecated',
  retired: 'Retired',
};

export const DRAFT_STATE_LABEL: Readonly<Record<string, string>> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  publishing: 'Publishing',
  published: 'Published',
  publish_failed: 'Publish failed',
};

export const PROPOSAL_STATUS_LABEL: Readonly<Record<string, string>> = {
  open: 'Open',
  accepted: 'Accepted',
  dismissed: 'Dismissed',
  withdrawn: 'Withdrawn',
};

export const PROPOSAL_CATEGORY_LABEL: Readonly<Record<string, string>> = {
  correction: 'Correction',
  failure: 'Failure',
  drift: 'Drift',
  gap: 'Gap',
};